import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, CheckCircle2, AlertCircle, ClipboardCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import api from '@/services/api';

interface ReceiveRepairModalProps {
  open: boolean;
  onClose: () => void;
  repairId: string;
  onSuccess: () => void;
}

export default function ReceiveRepairModal({ open, onClose, repairId, onSuccess }: ReceiveRepairModalProps) {
  const queryClient = useQueryClient();
  
  const [qualityCheckPassed, setQualityCheckPassed] = useState<boolean | null>(null);
  const [qualityCheckNotes, setQualityCheckNotes] = useState('');
  const [repairNotes, setRepairNotes] = useState('');
  const [actualCost, setActualCost] = useState('');
  const [partsReplaced, setPartsReplaced] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Receive repair mutation
  const receiveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        qualityCheckPassed,
        qualityCheckNotes,
        repairNotes,
        actualCost: actualCost ? parseFloat(actualCost) : null,
        partsReplaced,
      };
      const response = await api.post(`/repairs/${repairId}/receive`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repair', repairId] });
      queryClient.invalidateQueries({ queryKey: ['repairs'] });
      queryClient.invalidateQueries({ queryKey: ['sanalam-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['asset-statistics'] });
      resetForm();
      onSuccess();
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || 'Failed to receive repair');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (qualityCheckPassed === null) {
      setError('Please indicate if the quality check passed or failed');
      return;
    }

    receiveMutation.mutate();
  };

  const resetForm = () => {
    setQualityCheckPassed(null);
    setQualityCheckNotes('');
    setRepairNotes('');
    setActualCost('');
    setPartsReplaced('');
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-background rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto m-4">
        <Card className="border-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 border-b">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                Receive from Repair
              </CardTitle>
              <CardDescription>
                Mark the asset as received from the service provider
              </CardDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="w-5 h-5" />
            </Button>
          </CardHeader>
          
          <CardContent className="pt-6">
            {/* Error Alert */}
            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 rounded-md bg-destructive/10 border border-destructive/20">
                <AlertCircle className="w-5 h-5 text-destructive" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Quality Check */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <ClipboardCheck className="w-4 h-4" />
                  Quality Check <span className="text-destructive">*</span>
                </Label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    className={cn(
                      "p-4 rounded-lg border-2 transition-all text-center",
                      qualityCheckPassed === true
                        ? "border-green-500 bg-green-50"
                        : "border-input hover:border-green-300"
                    )}
                    onClick={() => setQualityCheckPassed(true)}
                  >
                    <CheckCircle2 className={cn(
                      "w-8 h-8 mx-auto mb-2",
                      qualityCheckPassed === true ? "text-green-500" : "text-muted-foreground"
                    )} />
                    <p className="font-medium text-sm">Passed</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Asset is working correctly
                    </p>
                  </button>
                  <button
                    type="button"
                    className={cn(
                      "p-4 rounded-lg border-2 transition-all text-center",
                      qualityCheckPassed === false
                        ? "border-red-500 bg-red-50"
                        : "border-input hover:border-red-300"
                    )}
                    onClick={() => setQualityCheckPassed(false)}
                  >
                    <AlertCircle className={cn(
                      "w-8 h-8 mx-auto mb-2",
                      qualityCheckPassed === false ? "text-red-500" : "text-muted-foreground"
                    )} />
                    <p className="font-medium text-sm">Failed</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Issues remain after repair
                    </p>
                  </button>
                </div>
              </div>

              {/* Quality Check Notes */}
              <div className="space-y-2">
                <Label htmlFor="qualityCheckNotes">
                  Quality Check Notes
                  {qualityCheckPassed === false && <span className="text-destructive"> *</span>}
                </Label>
                <textarea
                  id="qualityCheckNotes"
                  className={cn(
                    "w-full min-h-[80px] p-3 rounded-md border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring",
                    qualityCheckPassed === false && !qualityCheckNotes.trim() 
                      ? "border-destructive" 
                      : "border-input"
                  )}
                  placeholder={qualityCheckPassed === false 
                    ? "Describe the issues found during quality check..." 
                    : "Any observations from the quality check..."}
                  value={qualityCheckNotes}
                  onChange={(e) => setQualityCheckNotes(e.target.value)}
                />
                {qualityCheckPassed === false && !qualityCheckNotes.trim() && (
                  <p className="text-xs text-destructive">
                    Please describe why the quality check failed
                  </p>
                )}
              </div>

              {/* Actual Cost */}
              <div className="space-y-2">
                <Label htmlFor="actualCost">Actual Repair Cost</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <input
                    id="actualCost"
                    type="number"
                    step="0.01"
                    min="0"
                    className="w-full h-10 pl-7 pr-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="0.00"
                    value={actualCost}
                    onChange={(e) => setActualCost(e.target.value)}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Enter the final cost charged by the service provider
                </p>
              </div>

              {/* Parts Replaced */}
              <div className="space-y-2">
                <Label htmlFor="partsReplaced">Parts Replaced</Label>
                <input
                  id="partsReplaced"
                  type="text"
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="e.g., Battery, Screen, Keyboard"
                  value={partsReplaced}
                  onChange={(e) => setPartsReplaced(e.target.value)}
                />
              </div>

              {/* Repair Notes */}
              <div className="space-y-2">
                <Label htmlFor="repairNotes">Additional Notes</Label>
                <textarea
                  id="repairNotes"
                  className="w-full min-h-[60px] p-3 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Any additional notes about the repair..."
                  value={repairNotes}
                  onChange={(e) => setRepairNotes(e.target.value)}
                />
              </div>

              {/* Info Box */}
              <div className="p-4 rounded-md bg-blue-50 border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Receiving the repair will:
                </p>
                <ul className="text-sm text-blue-700 mt-2 list-disc list-inside space-y-1">
                  <li>Update the repair status to "Returned"</li>
                  <li>Record the received date and time</li>
                  <li>Log you as the receiving user</li>
                  {qualityCheckPassed === true && (
                    <li>The asset can be reassigned to the employee</li>
                  )}
                  {qualityCheckPassed === false && (
                    <li className="text-red-600">The asset may need further attention</li>
                  )}
                </ul>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={receiveMutation.isPending || qualityCheckPassed === null}
                >
                  {receiveMutation.isPending ? 'Processing...' : 'Receive Asset'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
